import { Heading } from "components/UI/typography/Heading";
import Image from "next/legacy/image";
import s from "./DownloadSampleModal.module.scss";
import { Button } from "components/UI/buttons/Button";
import { Input } from "components/UI/form/Input";
import { Modal, ModalProps } from "components/shared/Modal";
import { Formik } from "formik";
import * as Yup from "yup";
import { baseUrl } from "const";
import { usePathname } from "next/navigation";
import { FC, useState } from "react";
import cn from "classnames";
import { axios } from "utils/httpClient";
import { Link } from "@/components/shared/Link";

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please provide a valid email address")
    .required("Field is required!")
    .trim()
    .max(99, "Field should be no longer than 99 characters"),
});

type DownloadSampleModalProps = Omit<ModalProps, "children"> & {
  slug: string;
};

export const DownloadSampleModal: FC<DownloadSampleModalProps> = ({ open, setOpen, slug }) => {
  const [success, setSuccess] = useState<boolean | null>();

  const pathname = usePathname();

  return (
    <Modal
      open={open}
      setOpen={(value: boolean) => {
        if (!value) {
          setSuccess(null);
        }
        setOpen(value);
      }}
      type="downloadSample"
    >
      <div
        className={cn(s.main, {
          [s.mainHidden]: typeof success === "boolean",
        })}
      >
        <div className={s.modalTop}>
          <Heading level={3} tag={2} className={s.heading}>
            Please indicate where to send you
            <br />
            <span>the sample</span>
          </Heading>
          <div className={s.imgWrap}>
            <Image src="/img/sample/download-modal.svg" alt="Download sample" width={188} height={125} />
          </div>
        </div>
        <Formik
          initialValues={{ email: "" }}
          validationSchema={validationSchema}
          onSubmit={async ({ email }, { setSubmitting, resetForm }) => {
            axios
              .put(`/${slug}/clients`, {
                url: "https://" + baseUrl + pathname,
                email,
              })
              .then(() => {
                resetForm();
                setSubmitting(false);
                setSuccess(true);
              })
              .catch(() => {
                setSubmitting(false);
                setSuccess(false);
              });
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <form noValidate onSubmit={handleSubmit}>
              <div className={s.modalForm}>
                <Input
                  label="Email"
                  placeholder="Input your email"
                  type="email"
                  name="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  disabled={isSubmitting}
                  error={touched.email && errors.email?.length && errors.email}
                />
                <Button loading={isSubmitting} type="accent" tag="button" className={s.orderFormBtn}>
                  Send me sample
                </Button>
              </div>
            </form>
          )}
        </Formik>
        <p className={s.text}>
          By clicking Send me sample you agree to the{" "}
          <Link href="/terms-and-conditions">terms and conditions</Link> of our service. We&apos;ll not send
          you spam or irrelevant messages.
        </p>
      </div>
      {success === true && (
        <div className={s.result}>
          <div className={s.resultImg}>
            <Image src="/img/sample/download-modal.svg" alt="Download sample" width={188} height={125} />
          </div>
          <Heading level={3} className={s.resultHeading}>
            Success!
          </Heading>
          <p className={s.resultText}>You can check your shiny sample on your email.</p>
          <div className={s.resultBtn}>
            <Button tag="dashboardLink" href="/order" type="accent">
              Order similar
            </Button>
          </div>
        </div>
      )}
      {success === false && (
        <div className={s.result}>
          <div className={s.resultImg}>
            <Image src="/img/sample/download-modal.svg" alt="Download sample" width={188} height={125} />
          </div>
          <Heading level={3} className={s.resultHeading}>
            Something went wrong.
          </Heading>
          <p className={s.resultText}>Try entering a different email address.</p>
          <div className={s.resultBtn}>
            <Button tag="button" onClick={() => setSuccess(null)} type="accent">
              Try again
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
